// Session plumbing: one ApiClient for the app (overridable in tests) and a
// RequireAuth gate that resolves /api/v1/me once and redirects anonymous
// visitors to /login (carrying the origin location for post-login return).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApiClient, me, type UserInfo } from "./api";
import { Spinner } from "@/components/ui/spinner";
import { CairnMark } from "@/components/cairn-mark";

export const ApiCtx = createContext<ApiClient>(new ApiClient());

export function useApi(): ApiClient {
  return useContext(ApiCtx);
}

const SessionCtx = createContext<UserInfo | null>(null);
const SetUserCtx = createContext<(user: UserInfo) => void>(() => {});

/** The signed-in user (only inside RequireAuth). */
export function useUser(): UserInfo {
  const user = useContext(SessionCtx);
  if (!user) throw new Error("useUser outside RequireAuth");
  return user;
}

/** Update the in-memory session user (e.g. after editing your display name), so
 *  the shell reflects it without a reload. */
export function useSetUser(): (user: UserInfo) => void {
  return useContext(SetUserCtx);
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const api = useApi();
  const location = useLocation();
  const [state, setState] = useState<
    { kind: "loading" } | { kind: "authed"; user: UserInfo } | { kind: "anon" }
  >({ kind: "loading" });

  const setUser = useCallback((user: UserInfo) => {
    setState((s) => (s.kind === "authed" ? { kind: "authed", user } : s));
  }, []);

  useEffect(() => {
    let cancelled = false;
    me(api)
      .then((res) => {
        if (!cancelled) setState({ kind: "authed", user: res.user });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "anon" });
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (state.kind === "loading")
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-3 text-muted-foreground">
        <CairnMark className="size-9 text-primary" />
        <div className="flex items-center gap-2">
          <Spinner className="size-4" />
          <span className="font-mono text-xs tracking-wider uppercase">Loading cairn…</span>
        </div>
      </div>
    );
  if (state.kind === "anon") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return (
    <SessionCtx.Provider value={state.user}>
      <SetUserCtx.Provider value={setUser}>{children}</SetUserCtx.Provider>
    </SessionCtx.Provider>
  );
}
